import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import NavBar from "../../Componentes/NavBar";
import SortableTable from "../../Componentes/Utils/SortableTable";
import { useModal } from "../../contexts/ModalContext";
import useApi from "../../hooks/useApi";
import SidebarLicencasColaboradores from "./SidebarLicencasColaboradores";
import ModalNovoFornecLicenca from "./ModalNovoFornecLicenca";
import ModalNovoTipoLicenca from "./ModalNovoTipoLicenca";
import ModalLancarLicencaColaborador from "./ModalLancarLicencaColaborador";
import ModalDetalhesLicencasColaborador from "./ModalDetalhesLicencasColaborador";

const FORNECEDORES_ENDPOINT = "/Painel/API/InventarioTI/GetFornecLicenca/";
const COLABS_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/GetColabsLicenca/";
const RELATORIO_LICENCAS_ENDPOINT = "/Painel/API/InventarioTI/GerarRelatorioLicencas/";

const dash = (value) => {
  if (value == null || String(value).trim() === "") return "—";
  return String(value).trim();
};

const Container = styled.div`
  width: 100%;
  height: 100dvh;
  background-color: #242d34;
  display: flex;
  flex-direction: column;
`;

const BodyWrapper = styled.div`
  width: 100%;
  flex: 1;
  padding: 15px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 12px;
`;

const HeaderBar = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  color: #fff;
  font-size: 16px;
  letter-spacing: 0.5px;
`;

const Badge = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13px;
`;

const FilterInput = styled.input`
  height: 40px;
  flex: 1;
  min-width: 240px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 0 12px;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: rgba(0, 123, 255, 0.9);
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const TableShell = styled.div`
  width: 100%;
  flex: 1;
  background-color: #ffffff;
  border-radius: 15px;
  overflow: hidden;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(p) => (p.$ativo ? "#22c55e" : "#ef4444")};
  flex-shrink: 0;
`;

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export default function LicencasColaboradores() {
  const { openModal, isModalOpen, closeModal } = useModal();
  const api = useApi();

  const [loading, setLoading] = useState(true);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [fornecedores, setFornecedores] = useState([]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selectedColaborador, setSelectedColaborador] = useState(null);

  const requestColaboradores = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(COLABS_LICENCA_ENDPOINT);
      const list = Array.isArray(data) ? data : data?.data ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao carregar colaboradores.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  const requestFornecedores = useCallback(async () => {
    try {
      const { data } = await api.get(FORNECEDORES_ENDPOINT);
      const list = Array.isArray(data) ? data : data?.data ?? [];
      setFornecedores(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  useEffect(() => {
    requestColaboradores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const colaboradores = useMemo(() => {
    return (rows ?? []).map((r) => {
      const centroCustoDesc =
        r.centro_custo_desc != null && String(r.centro_custo_desc).trim() !== ""
          ? String(r.centro_custo_desc).trim()
          : "";
      const centroCusto =
        r.centro_custo != null && String(r.centro_custo).trim() !== ""
          ? String(r.centro_custo).trim()
          : "";

      return {
        matricula: dash(r.matricula),
        nome: dash(r.nome),
        funcao: dash(r.funcao),
        centro_custo: centroCustoDesc || centroCusto || "—",
        qtd_licencas: Number(r.qtd_licencas ?? 0),
        demitido: Boolean(r.demitido),
      };
    });
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toUpperCase();
    const base = !query
      ? colaboradores
      : colaboradores.filter((c) => {
          const hay = [
            c.matricula,
            c.nome,
            c.funcao,
            c.centro_custo,
            String(c.qtd_licencas),
            c.demitido ? "DESLIGADO" : "ATIVO",
          ]
            .filter(Boolean)
            .join(" ")
            .toUpperCase();
          return hay.includes(query);
        });

    return [...base].sort((a, b) => {
      if (a.demitido !== b.demitido) {
        return a.demitido ? -1 : 1;
      }

      const nomeA = a.nome === "—" ? "" : a.nome;
      const nomeB = b.nome === "—" ? "" : b.nome;
      return nomeA.localeCompare(nomeB, "pt-BR", { sensitivity: "base" });
    });
  }, [colaboradores, q]);

  const totalLicencas = useMemo(
    () => filtered.reduce((acc, c) => acc + (Number(c.qtd_licencas) || 0), 0),
    [filtered]
  );

  const columns = useMemo(
    () => [
      {
        id: "status",
        header: "",
        accessor: () => null,
        sortable: false,
        width: "44px",
        align: "center",
        render: (_v, row) => (
          <StatusCell>
            <StatusDot
              $ativo={!row.demitido}
              title={row.demitido ? "Desligado" : "Ativo"}
              aria-label={row.demitido ? "Colaborador desligado" : "Colaborador ativo"}
            />
          </StatusCell>
        ),
      },
      {
        id: "matricula",
        header: "MATRÍCULA",
        accessor: (r) => r.matricula,
        width: "110px",
      },
      {
        id: "nome",
        header: "NOME",
        accessor: (r) => r.nome,
        width: "260px",
      },
      {
        id: "funcao",
        header: "FUNÇÃO",
        accessor: (r) => r.funcao,
        width: "220px",
      },
      {
        id: "centro_custo",
        header: "CENTRO DE CUSTO",
        accessor: (r) => r.centro_custo,
        width: "240px",
      },
      {
        id: "qtd_licencas",
        header: "QTD. LICENÇAS",
        accessor: (r) => r.qtd_licencas,
        sortValue: (r) => Number(r.qtd_licencas) || 0,
        width: "130px",
        align: "center",
      },
    ],
    []
  );

  const handleOpenSidebar = () => openModal("sidebarLicencasColaboradores");

  const handleLancarLicenca = () => {
    closeModal();
    openModal("modalLancarLicencaColaborador");
  };

  const handleNovoFornecedor = () => {
    closeModal();
    openModal("modalNovoFornecLicenca");
  };

  const handleNovoTipoLicenca = () => {
    closeModal();
    openModal("modalNovoTipoLicenca");
  };

  const handleFornecedorCreated = async () => {
    await requestFornecedores();
  };

  const handleLicencaLancada = async () => {
    await requestColaboradores();
  };

  const handleLicencasChanged = async () => {
    await requestColaboradores();
  };

  const handleRowClick = (row) => {
    setSelectedColaborador(row);
    openModal("modalDetalhesLicencasColaborador");
  };

  const gerarRelatorio = async () => {
    if (loadingRelatorio) return;

    setLoadingRelatorio(true);

    try {
      const response = await api.get(RELATORIO_LICENCAS_ENDPOINT, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, "")
        .slice(0, 14);
      link.href = url;
      link.setAttribute("download", `licencas_colaboradores_${stamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      closeModal();
    } catch (err) {
      console.error(err);
      let msg = "Erro ao gerar relatório.";
      if (err?.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await err.response.data.text());
          if (parsed?.message) msg = parsed.message;
        } catch {
          /* mantém mensagem padrão */
        }
      } else if (err?.response?.data?.message) {
        msg = err.response.data.message;
      }
      alert(msg);
    } finally {
      setLoadingRelatorio(false);
    }
  };

  return (
    <>
      <Container>
        <NavBar
          title="INVENTÁRIO T.I. — LICENÇAS"
          navTo="/modulo/inventario-ti"
          setSideBar={handleOpenSidebar}
        />
        <BodyWrapper>
          <HeaderBar>
            <Title>Colaboradores</Title>
            <Badge>Total: {filtered.length}</Badge>
            <Badge>Licenças ativas: {totalLicencas}</Badge>
            <FilterInput
              placeholder="Pesquisar por matrícula, nome, função, centro de custo..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </HeaderBar>

          <TableShell>
            <SortableTable
              columns={columns}
              data={filtered}
              isLoading={loading}
              loadingText="Carregando colaboradores..."
              onRowClick={handleRowClick}
              rowCursor="pointer"
              emptyText="NENHUM COLABORADOR COM LICENÇA ENCONTRADO"
            />
          </TableShell>
        </BodyWrapper>
      </Container>

      {isModalOpen("sidebarLicencasColaboradores") && (
        <SidebarLicencasColaboradores
          onClose={() => closeModal()}
          onLancarLicenca={handleLancarLicenca}
          onNovoFornecedor={handleNovoFornecedor}
          onNovoTipoLicenca={handleNovoTipoLicenca}
          onGerarRelatorio={gerarRelatorio}
          loadingRelatorio={loadingRelatorio}
        />
      )}

      {isModalOpen("modalDetalhesLicencasColaborador") && selectedColaborador && (
        <ModalDetalhesLicencasColaborador
          colaborador={selectedColaborador}
          onClose={() => {
            closeModal();
            setSelectedColaborador(null);
          }}
          onChanged={handleLicencasChanged}
        />
      )}

      {isModalOpen("modalLancarLicencaColaborador") && (
        <ModalLancarLicencaColaborador
          onClose={() => closeModal()}
          onSaved={handleLicencaLancada}
        />
      )}

      {isModalOpen("modalNovoFornecLicenca") && (
        <ModalNovoFornecLicenca
          onClose={() => closeModal()}
          onCreated={handleFornecedorCreated}
        />
      )}

      {isModalOpen("modalNovoTipoLicenca") && (
        <ModalNovoTipoLicenca
          onClose={() => closeModal()}
          onCreated={handleFornecedorCreated}
          fornecedores={fornecedores}
          onRequestFornecedores={requestFornecedores}
        />
      )}
    </>
  );
}
